import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import SeoHead from "@/components/SeoHead";
import { SITE_URL } from "@/seo/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusTimeline, CLAIM_STAGES, SATTLEREI_STAGES, TINT_STAGES } from "@/components/StepProgress";
import { FunZone } from "@/components/funzone/FunZone";
import { MagnifyingGlass, CarProfile, CalendarBlank, ArrowRight, SmileyMeh, Armchair, XCircle, SealCheck } from "@phosphor-icons/react";

const eur = (n) => Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 });
const fmtDay = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "—";

export default function StatusPage() {
  const navigate = useNavigate();
  const { trackingCode } = useParams();
  const [code, setCode] = useState(trackingCode || "");
  const [data, setData] = useState(null);
  const [sattlerei, setSattlerei] = useState(null);
  const [tint, setTint] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchStatus = async (c) => {
    if (!c) return;
    const upper = c.toUpperCase().trim();
    setLoading(true);
    setNotFound(false);
    try {
      if (upper.startsWith("SA-")) {
        const { data } = await api.get(`/sattlerei/track/${upper}`);
        setData(null); setTint(null);
        setSattlerei(data);
      } else if (upper.startsWith("TB-")) {
        const { data } = await api.get(`/tint/track/${upper}`);
        setData(null); setSattlerei(null);
        setTint(data);
      } else {
        const { data } = await api.get(`/claims/track/${upper}`);
        setSattlerei(null); setTint(null);
        setData(data);
      }
    } catch (e) {
      setData(null);
      setSattlerei(null);
      setTint(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trackingCode) fetchStatus(trackingCode);
  }, [trackingCode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.trim()) navigate(`/status/${code.trim().toUpperCase()}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoHead
        title="Auftragsstatus live verfolgen | Servus Autoglas"
        description="Verfolgen Sie den Status Ihrer Schadenmeldung oder Buchung bei Servus Autoglas in Echtzeit — einfach Tracking-Code eingeben."
        canonical={`${SITE_URL}/status`}
        noindex={!!trackingCode}
      />
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Status verfolgen</h1>
          <p className="text-slate-600 mt-2">Geben Sie Ihren Tracking-Code ein (z.B. AG-7Y3K) — Scheibentönung: TB-Nummer, Sattlerei: SA-Nummer.</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="AG-XXXXXX"
            className="h-14 text-lg font-mono tracking-wider"
            data-testid="status-input-code"
          />
          <Button type="submit" disabled={loading} className="h-14 px-6 w-full sm:w-auto bg-blue-600 hover:bg-blue-700" data-testid="status-search-button">
            <MagnifyingGlass size={18} className="mr-2" /> Suchen
          </Button>
        </form>

        {notFound && !data && !sattlerei && !tint && (
          <div className="max-w-xl mx-auto rounded-2xl bg-white border border-slate-200 shadow-sm p-6 text-center animate-fade-up" data-testid="status-not-found">
            <SmileyMeh size={36} className="text-slate-400 mx-auto" aria-hidden="true" />
            <p className="mt-2 font-semibold text-slate-800">Hmm … diesen Tracking-Code konnten wir nicht finden.</p>
            <p className="mt-1 text-sm text-slate-500">Prüfe bitte kurz die Eingabe und versuche es noch einmal.</p>
          </div>
        )}

        {data && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6 animate-fade-up">
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoBox icon={CarProfile} label="Fahrzeug" value={`${data.manufacturer || ""} ${data.model || ""}`.trim() || "—"} sub={data.license_plate} />
              <InfoBox
                icon={CalendarBlank}
                label="Termin"
                value={data.appointment_date ? new Date(data.appointment_date).toLocaleDateString("de-DE") : "noch nicht gebucht"}
                sub={data.appointment_time}
              />
            </div>

            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-bold text-slate-900 mb-5 text-lg">Status-Verlauf</h3>
              <StatusTimeline stages={CLAIM_STAGES} currentStatus={data.status} history={data.status_history} />
            </div>

            <div className="border-t border-slate-200 pt-5 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Erstellt am {new Date(data.created_at).toLocaleString("de-DE")}
              </div>
              <Button variant="outline" onClick={() => fetchStatus(code)} data-testid="status-refresh">
                Aktualisieren <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {sattlerei && (
          <div className="bg-white rounded-2xl border-2 border-[#a9e3b6] shadow-sm p-6 sm:p-8 space-y-6 animate-fade-up" data-testid="sattlerei-status-result">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#55C96B] text-white text-[11px] font-bold uppercase tracking-widest">
                <Armchair size={13} weight="fill" /> Sattlerei
              </span>
              <span className="font-mono font-semibold text-slate-700">{sattlerei.booking_number}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoBox icon={CarProfile} label="Fahrzeug" value={`${sattlerei.vehicle?.manufacturer || ""} ${sattlerei.vehicle?.model || ""}`.trim() || "—"} sub={sattlerei.license_plate} />
              <InfoBox icon={Armchair} label="Leistung" value={sattlerei.service_label || "—"} />
              <InfoBox icon={CalendarBlank} label="Fahrzeugabgabe" value={fmtDay(sattlerei.date)} sub={sattlerei.time ? `${sattlerei.time} Uhr` : null} />
              <InfoBox icon={CalendarBlank} label="Voraussichtliche Abholung" value={fmtDay(sattlerei.pickup_date)} />
            </div>
            <div className="rounded-xl bg-[#EAF8ED] border border-[#cdeed6] p-4 flex flex-wrap gap-x-8 gap-y-2 text-sm" data-testid="sattlerei-status-payment">
              <div>
                <span className="text-slate-500">Preis: </span>
                <strong className="text-slate-900">{sattlerei.price ? `${eur(sattlerei.price)} € inkl. MwSt.` : `ab ${eur(sattlerei.price_from)} € — nach Prüfung`}</strong>
              </div>
              {sattlerei.price ? (
                <>
                  <div><span className="text-slate-500">Bezahlt: </span><strong className="text-[#249143]">{eur(sattlerei.paid_amount)} €</strong></div>
                  <div><span className="text-slate-500">Restbetrag: </span><strong className="text-slate-900">{eur(Math.max(0, sattlerei.price - (sattlerei.paid_amount || 0)))} €</strong></div>
                </>
              ) : null}
            </div>
            {sattlerei.status === "storniert" ? (
              <div className="rounded-xl bg-red-50 border border-red-100 p-4 flex items-center gap-3 text-sm text-red-700" data-testid="sattlerei-status-cancelled">
                <XCircle size={22} weight="fill" className="flex-shrink-0" />
                Diese Buchung wurde storniert. Bei Fragen erreichen Sie uns unter +49 621 527429.
              </div>
            ) : (
              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-bold text-slate-900 mb-5 text-lg">Status-Verlauf</h3>
                <StatusTimeline stages={SATTLEREI_STAGES} currentStatus={sattlerei.status} history={sattlerei.status_history} />
              </div>
            )}
            <div className="border-t border-slate-200 pt-5 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Gebucht am {new Date(sattlerei.created_at).toLocaleString("de-DE")}
              </div>
              <Button variant="outline" onClick={() => fetchStatus(code)} data-testid="sattlerei-status-refresh">
                Aktualisieren <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {tint && (
          <div className="bg-white rounded-2xl border-2 border-pink-200 shadow-sm p-6 sm:p-8 space-y-6 animate-fade-up" data-testid="tint-status-result">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-600 text-white text-[11px] font-bold uppercase tracking-widest">
                <SealCheck size={13} weight="fill" /> Scheibentönung
              </span>
              <span className="font-mono font-semibold text-slate-700">{tint.booking_number}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoBox icon={CarProfile} label="Fahrzeug" value={`${tint.vehicle?.manufacturer || ""} ${tint.vehicle?.model || ""}`.trim() || "—"} sub={tint.license_plate || tint.vehicle_class_label} />
              <InfoBox icon={SealCheck} label="Leistung" value="Tönung ab B-Säule inkl. Heckscheibe" sub={tint.film?.name} />
              <InfoBox icon={CalendarBlank} label="Montagstermin" value={fmtDay(tint.date)} sub={tint.time ? `${tint.time} Uhr` : null} />
              <InfoBox icon={CalendarBlank} label="Standort" value="Frankenthaler Str. 98" sub="67059 Ludwigshafen" />
            </div>
            <div className="rounded-xl bg-pink-50 border border-pink-100 p-4 flex flex-wrap gap-x-8 gap-y-2 text-sm" data-testid="tint-status-payment">
              <div><span className="text-slate-500">Festpreis: </span><strong className="text-slate-900">{eur(tint.price)} € inkl. MwSt.</strong></div>
              <div>
                <span className="text-slate-500">Anzahlung (25 %): </span>
                <strong className={tint.deposit_paid ? "text-emerald-600" : "text-slate-900"}>
                  {eur(tint.deposit_amount)} € {tint.deposit_paid ? "— bezahlt" : "— offen"}
                </strong>
              </div>
              <div><span className="text-slate-500">Restbetrag vor Ort: </span><strong className="text-slate-900">{eur(tint.price - (tint.deposit_paid ? tint.deposit_amount : 0))} €</strong></div>
            </div>
            {tint.status === "storniert" ? (
              <div className="rounded-xl bg-red-50 border border-red-100 p-4 flex items-center gap-3 text-sm text-red-700" data-testid="tint-status-cancelled">
                <XCircle size={22} weight="fill" className="flex-shrink-0" />
                Diese Buchung wurde storniert. Bei Fragen erreichen Sie uns unter +49 621 527429.
              </div>
            ) : (
              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-bold text-slate-900 mb-5 text-lg">Status-Verlauf</h3>
                <StatusTimeline
                  stages={TINT_STAGES.filter((s) => s.key !== "anzahlung_erhalten" || tint.deposit_paid || tint.status === "anzahlung_erhalten")}
                  currentStatus={tint.status}
                  history={tint.status_history}
                />
              </div>
            )}
            <div className="border-t border-slate-200 pt-5 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Gebucht am {new Date(tint.created_at).toLocaleString("de-DE")}
              </div>
              <Button variant="outline" onClick={() => fetchStatus(code)} data-testid="tint-status-refresh">
                Aktualisieren <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <FunZone />
    </div>
  );
}

const InfoBox = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/40">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
      <Icon size={14} /> {label}
    </div>
    <div className="text-lg font-semibold text-slate-900 mt-1">{value}</div>
    {sub && <div className="text-sm text-slate-500">{sub}</div>}
  </div>
);
